import { useEffect, useState } from 'react';

import { ListboxButton } from '@headlessui/react';
import { isNil } from 'lodash';
import { useLocation } from 'react-router-dom';

import { AngleDown, AngleUp } from '@assets/icons';
import { Button } from '@components/index';
import { useGetUser } from '@hooks/use-get-user';
import { AppRoutes } from '@routes/constants';
import { getAuthToken, LocalStorageKeys } from '@utils/auth';
import { useDerivedState } from '@utils/use-derived-state';

import SelectWorkspaceDropdown from './SelectWorkspaceDropdown';
import { useAuthCodeInSSO } from '../hooks/use-auth-code-in-sso';
import { useListWorkspaces } from '../hooks/use-list-workspaces';
import { useSelectWorkspace } from '../hooks/use-select-workspace';
import { Workspace } from '../types';

export const SelectWorkspaceRefComponent = ({
  label,
  isOpen,
}: {
  label: string;
  isOpen: boolean;
}) => {
  return (
    <ListboxButton className="w-full cursor-pointer focus:outline-none">
      <div className="w-full h-12 flex justify-between items-center px-2 border border-gray-4 rounded-md focus:outline-none">
        <div className="flex justify-start items-center truncate font-semibold whitespace-nowrap">
          {!label && (
            <div className="text-gray-3 text-sm">Select workspace</div>
          )}
          {!isNil(label) && (
            <span className="truncate pl-2 text-sm">{label}</span>
          )}
        </div>
        {isOpen ? (
          <AngleUp className="text-gray-1" />
        ) : (
          <AngleDown className="text-gray-1" />
        )}
      </div>
    </ListboxButton>
  );
};

const SelectWorkspaceForm = () => {
  const location = useLocation();

  // Check for authCode query parameter
  const queryParams = new URLSearchParams(location.search);
  const authCode = queryParams.get('authCode');

  const [workspaceSearchKeyword, setWorkspaceSearchKeyword] = useState<
    string | null
  >(null);
  const [activeParentWorkspaceId, setActiveParentWorkspaceId] = useState<
    number | null
  >(null);

  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(
    null,
  );

  const [isTokenNotAvailable, setIsTokenNotAvailable] =
    useDerivedState<boolean>(isNil(getAuthToken()));

  const { mutateAsync: exchangeAuthCodeWithToken } = useAuthCodeInSSO({
    onSuccess: () => {
      setIsTokenNotAvailable(true);
    },
  });

  useEffect(() => {
    if (authCode) exchangeAuthCodeWithToken({ authCode });
  }, [authCode, exchangeAuthCodeWithToken]);

  useGetUser({
    enabled: !isTokenNotAvailable,
  });

  const {
    workspaceList,
    isLoading: isLoadingWorkspaces,
    hasNextPage,
    fetchNextPage,
  } = useListWorkspaces({
    isActive: true,
    searchKeyword: workspaceSearchKeyword,
    parentClientId: activeParentWorkspaceId,
    isEnabled: !isTokenNotAvailable,
  });

  const { isLoading, refetch: refetchWorkspace } = useSelectWorkspace({
    clientUuid: selectedWorkspace?.uuid as string,
    enabled: false,
    onSuccess: () => {
      window.location.href = `${window.location.origin}/${selectedWorkspace?.uuid}${AppRoutes.BRAND_LIBRARY}`;
      localStorage.setItem(LocalStorageKeys.IS_LOGGED_IN, 'true');
    },
  });

  const onWorkspaceOptionSelect = (option: Workspace) => {
    setSelectedWorkspace(option);
  };

  const onWorkspaceSubmit = () => {
    refetchWorkspace();
  };

  return (
    <div className="w-full">
      <div className="text-sm mb-1 font-medium">Workspace</div>
      <SelectWorkspaceDropdown
        options={workspaceList}
        isLoadingOptions={isLoadingWorkspaces}
        hasMoreOptions={hasNextPage}
        fetchMoreOptions={fetchNextPage}
        selectedOption={selectedWorkspace}
        onOptionSelect={onWorkspaceOptionSelect}
        setSearchKeyword={setWorkspaceSearchKeyword}
        activeParentId={activeParentWorkspaceId}
        setActiveParentId={setActiveParentWorkspaceId}
        PopperRefComponent={SelectWorkspaceRefComponent}
        classOverride="min-w-[256px] max-h-[215px]"
        isLogin={true}
      />
      <div className="flex items-center mt-4">
        <Button
          variant="primary-blue"
          label="Go to workspace"
          isDisabled={isNil(selectedWorkspace) || isLoading}
          isLoading={isLoading}
          onClick={onWorkspaceSubmit}
        />
      </div>
    </div>
  );
};

export default SelectWorkspaceForm;
